// Update the NotificationModal component in NotificationModal.jsx

import React, { useState, useMemo } from "react";
import Select from "react-select";
import { Info } from "lucide-react"; // Add this import

const NotificationModal = ({ users, onSubmit, onClose, isOpen, Modal }) => {
  const [subject, setSubject] = useState("");
  const [note, setNote] = useState("");
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [notificationType, setNotificationType] = useState("manual");
  const [forceEmail, setForceEmail] = useState(false); // Add this state

  // Notification type options
  const notificationTypes = [
    { value: "manual", label: "General Message" },
    { value: "residencyMilestone", label: "Residency Milestone" },
    { value: "sobrietyBirthday", label: "Sobriety Birthday" },
    { value: "birthday", label: "Birthday" },
  ];

  const groupedOptions = useMemo(() => {
    const roleOptions = [
      { value: "All", label: "All Users" },
      { value: "Admin", label: "Admin" },
      { value: "Staff", label: "Staff" },
      { value: "Leader", label: "Leader" },
      { value: "Resident", label: "Resident" },
    ];

    const userOptions = users.map((user) => ({
      value: user.id,
      label: `${user.firstName} ${user.lastName} (${user.role})`,
    }));

    return [
      { label: "Roles", options: roleOptions },
      { label: "Individual Users", options: userOptions },
    ];
  }, [users]);

  const customStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: "rgba(31, 41, 55, 0.5)",
      borderColor: "#4B5563",
      color: "white",
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "rgba(31, 41, 55, 0.9)",
    }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      backgroundColor: isSelected
        ? "#1F2937"
        : isFocused
        ? "rgba(31, 41, 55, 0.7)"
        : "transparent",
      color: isSelected ? "#10B981" : "#D1D5DB",
    }),
  };

  const handleSubmit = () => {
    if (!subject || !note || selectedRecipients.length === 0) {
      alert("Please fill out all fields and select at least one recipient.");
      return;
    }

    // Pass notification type and forceEmail flag to the submit handler
    onSubmit(subject, note, selectedRecipients, notificationType, forceEmail);

    // Reset form
    setSubject("");
    setNote("");
    setSelectedRecipients([]);
    setNotificationType("manual");
    setForceEmail(false);
    onClose();
  };

  const handleRecipientsChange = (selectedOptions) => {
    if (!selectedOptions) {
      setSelectedRecipients([]);
      return;
    }

    setSelectedRecipients(selectedOptions.map((option) => option.value));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Send Notification">
      <div className="space-y-4 p-4">
        <div>
          <label className="block text-white mb-2">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full px-3 py-2 text-white border rounded-lg bg-slate-800"
            placeholder="Enter subject"
          />
        </div>

        <div>
          <label className="block text-white mb-2">Note</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full px-3 py-2 text-white border rounded-lg bg-slate-800"
            rows="3"
            placeholder="Enter your message"
          />
        </div>

        <div>
          <label className="block text-white mb-2">Notification Type</label>
          <select
            value={notificationType}
            onChange={(e) => setNotificationType(e.target.value)}
            className="w-full px-3 py-2 text-white border rounded-lg bg-slate-800"
          >
            {notificationTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-white mb-2">Recipients</label>
          <Select
            options={groupedOptions}
            isMulti
            value={selectedRecipients.map((id) =>
              groupedOptions
                .flatMap((group) => group.options)
                .find((option) => option.value === id)
            )}
            onChange={handleRecipientsChange}
            placeholder="Search for users or select roles..."
            styles={customStyles}
            classNamePrefix="react-select"
          />
        </div>

        {/* Email Options */}
        <div className="p-3 bg-slate-800/50 rounded-lg">
          <div className="flex items-center space-x-2">
            <input
              id="force-email"
              type="checkbox"
              checked={forceEmail}
              onChange={(e) => setForceEmail(e.target.checked)}
              className="w-4 h-4 text-blue-500 bg-slate-800 border-slate-500 rounded focus:ring-blue-500"
            />
            <label
              htmlFor="force-email"
              className="flex items-center space-x-2 text-sm font-medium text-slate-300"
            >
              <span>Send email notification to all recipients</span>
              <div className="group relative inline-block">
                <Info size={16} className="text-slate-400 cursor-help" />
                <div className="invisible group-hover:visible absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 p-2 w-64 bg-slate-700 text-slate-200 text-xs rounded-lg shadow-lg z-10">
                  This will send an email to all recipients regardless of their
                  notification preferences. Use this for urgent or important
                  notifications.
                </div>
              </div>
            </label>
          </div>
          <p className="text-xs text-slate-400 mt-2 ml-6">
            Otherwise, emails will only be sent to users who have enabled email
            notifications for this type in their settings.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-700">
        <button
          onClick={onClose}
          className="px-4 py-2 text-red-400 hover:text-red-300 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg"
        >
          Submit
        </button>
      </div>
    </Modal>
  );
};

export default NotificationModal;
